import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { NativeSelect } from '../ui/NativeSelect';
import { useTemplatesStore } from '@/store/templatesStore';
import { ListItem } from '@/types/lists';

const templateItemSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  description: z.string().optional(),
  estimatedMinutes: z.coerce.number().min(1).optional().or(z.literal('')),
  difficulty: z.enum(['easy', 'medium', 'hard', '']).optional(),
});

type TemplateItemFormInputs = z.infer<typeof templateItemSchema>;

type TemplateItem = Omit<ListItem, 'id' | 'createdAt' | 'priority'>;

interface TemplateItemEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  itemIndex: number;
  item: TemplateItem;
}

export function TemplateItemEditForm({
  isOpen,
  onClose,
  templateId,
  itemIndex,
  item,
}: TemplateItemEditFormProps) {
  const { updateTemplateItem } = useTemplatesStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<TemplateItemFormInputs>({
    resolver: zodResolver(templateItemSchema),
    defaultValues: {
      content: '',
      description: '',
      estimatedMinutes: '',
      difficulty: '',
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (isOpen && item) {
      reset({
        content: item.content || '',
        description: item.description || '',
        estimatedMinutes: item.estimatedMinutes || '',
        difficulty: item.difficulty || '',
      });
    }
  }, [isOpen, item, reset]);

  const handleFormSubmit = async (data: TemplateItemFormInputs) => {
    try {
      const difficultyValue = data.difficulty as string;
      await updateTemplateItem(templateId, itemIndex, {
        content: data.content,
        description: data.description || undefined,
        estimatedMinutes: data.estimatedMinutes ? Number(data.estimatedMinutes) : undefined,
        difficulty: difficultyValue && difficultyValue !== '' ? difficultyValue as 'easy' | 'medium' | 'hard' : undefined,
      });

      handleClose();
    } catch (error) {
      console.error('Error updating template item:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title="Edit Template Item"
      description="Update the item details"
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Content */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Content *</label>
          <Input
            {...register('content')}
            placeholder="Item content..."
            error={errors.content?.message}
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Description (Optional)</label>
          <textarea
            {...register('description')}
            placeholder="Additional details..."
            className="w-full min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
        </div>

        {/* Estimated Minutes */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Estimated Time (minutes)</label>
          <Input
            {...register('estimatedMinutes')}
            type="number"
            placeholder="e.g., 30"
            min="1"
            max="999"
          />
        </div>

        {/* Difficulty */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Difficulty</label>
          <NativeSelect {...register('difficulty')}>
            <option value="">None</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </NativeSelect>
        </div>
      </form>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4 bg-background sticky bottom-0">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Modal>
  );
}
